import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import numeral from 'numeral';

import DataTable from '../../../../components/Datatable';
import Timeline from '../../../../components/Timeline';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import MetricCards from '../../GscTools/components/MetricCards';
import { METRIC_COLORS } from '../../GscTools/format';
import { metricsFor, reportsPosition } from '../../GscTools/metrics';
import { COVERAGE, hourLabel } from '../hours';
import { movementColumns } from '../keys';
import { movementRows } from '../rows';

/** The dashed baseline is the same hue as the current line, lightened so it reads as behind it. */
const BASELINE_ALPHA = '66';

/**
 * Block 02 — the window's hours as two lines: now, and the same hours a week ago.
 *
 * A week ago rather than yesterday, because an hourly series is mostly shape: traffic at 03:00 is
 * not traffic at 15:00, and the only honest thing to lay over Tuesday 15:00 is another Tuesday
 * 15:00. The two series are aligned on the exact instant, seven days apart, so the pairing
 * survives a daylight-saving change that would break an hour-of-day match.
 *
 * The cards above are the comparable hours only, always — whatever the chart is showing. A total
 * that included the trailing still-collecting hours would compare a partial hour against a
 * settled one and make every window look like a decline. When the reader asks to see those hours
 * anyway, they appear in the chart, drawn as the provisional values they are, and the cards go on
 * reporting the hours that can actually be compared.
 *
 * Gaps stay gaps. An hour Search Console did not return is a null, so the line breaks there
 * rather than running straight through an hour nobody has a figure for.
 */
class HourlyMovement extends Component {
    constructor(props) {
        super(props);
        this.axisLabel = this.axisLabel.bind(this);
        this.searchFunction = this.searchFunction.bind(this);
    }

    /** CTR arrives as a fraction; Timeline's axis and tooltip speak in percent. */
    chartValue(value) {
        if (value === null || value === undefined) return null;
        return this.props.metric === 'ctr' ? value * 100 : value;
    }

    axisLabel(value) {
        const { metric } = this.props;
        if (metric === 'ctr') return `${numeral(value).format('0.00')}%`;
        if (metric === 'position') return numeral(value).format('0.0');
        return numeral(value).format('0 a');
    }

    /**
     * The x labels: the clock time, carrying its date whenever the day turns over.
     *
     * A 24-hour window crosses midnight, and two 15:00s a day apart would otherwise be
     * indistinguishable on the axis.
     */
    labels(rows) {
        let lastDay = null;

        return rows.map((row) => {
            const day = row.hour.format('YYYY-MM-DD');
            const withDate = day !== lastDay;
            lastDay = day;
            return hourLabel(row.hour, { withDate });
        });
    }

    chartData(rows) {
        const { t, metric, scope } = this.props;
        const color = METRIC_COLORS[metric];

        const datasets = [
            {
                label: t('tools.latest_hours.movement.current'),
                type: 'line',
                data: rows.map((row) => this.chartValue(row.currentValue)),
                borderColor: color,
                backgroundColor: color,
                borderWidth: 2.5,
                yAxisID: 'y',
                tension: 0.25,
                // A provisional hour is drawn hollow and the run into it dashed, so the part of
                // the line that will still be revised looks unlike the part that will not.
                pointBackgroundColor: rows.map((row) => (row.provisional ? '#ffffff' : color)),
                pointBorderColor: color,
                pointBorderWidth: rows.map((row) => (row.provisional ? 2 : 1)),
                segment: {
                    borderDash: (ctx) => (rows[ctx.p1DataIndex]?.provisional ? [5, 4] : undefined),
                },
            },
        ];

        if (scope.comparing) {
            datasets.push({
                label: t('tools.latest_hours.movement.prior'),
                type: 'line',
                data: rows.map((row) => this.chartValue(row.priorValue)),
                borderColor: `${color}${BASELINE_ALPHA}`,
                backgroundColor: 'transparent',
                borderWidth: 2,
                borderDash: [6, 5],
                yAxisID: 'y',
                tension: 0.25,
            });
        }

        return { labels: this.labels(rows), datasets };
    }

    searchFunction(nodes, search) {
        if (!search) return nodes;
        const term = search.toLowerCase();
        return nodes.filter((row) => row.key?.toLowerCase().includes(term));
    }

    render() {
        const { t, scope, hours, totals, metric, onMetric } = this.props;

        const hasPosition = reportsPosition(scope.searchType);
        const rows = movementRows(hours, { coverage: scope.coverage, metric, hasPosition });
        const columns = movementColumns(t, { metric, comparing: scope.comparing });
        const label = t(`tools.gsc.metrics.${metric}`);

        const comparison = !scope.comparing
            ? { comparable: false, reason: 'no_baseline' }
            : totals.hours
            ? { comparable: true, reason: null }
            : { comparable: false, reason: 'incomplete_coverage' };

        const showingProvisional = scope.coverage === COVERAGE.ALL;

        return (
            <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                <div className="card-header d-flex justify-content-between">
                    <div>
                        <p className="card-title">{t('tools.latest_hours.movement.title')}</p>
                        <p className="gsc-tool-sub">
                            {t('tools.latest_hours.movement.description')}
                        </p>
                    </div>
                </div>

                <div className="card-body">
                    {showingProvisional && (
                        <div className="gsc-tool-banner">
                            {t('tools.latest_hours.movement.provisional_shown')}
                        </div>
                    )}

                    <MetricCards
                        metrics={metricsFor(scope.searchType)}
                        totals={totals.current}
                        priorTotals={totals.prior}
                        comparison={comparison}
                        selectedMetric={metric}
                        onSelectMetric={onMetric}
                        noteFor={(name, defaultNote) =>
                            totals.hours
                                ? t('tools.latest_hours.movement.over_hours', {
                                      hours: totals.hours,
                                  })
                                : defaultNote
                        }
                    />

                    <p className="gsc-tool-hint">{t('tools.latest_hours.movement.pick_card')}</p>

                    {!rows.length ? (
                        <div className="gsc-tool-load-state">
                            <span className="gsc-tool-load-icon" aria-hidden="true">
                                <i className="icon-area-chart" />
                            </span>
                            <h4>{t('tools.latest_hours.movement.empty_title')}</h4>
                            <p>{t('tools.latest_hours.movement.empty')}</p>
                        </div>
                    ) : (
                        <div className="gsc-tool-trend">
                            <Timeline
                                data={rows}
                                dontShowMetrics
                                chartSize="lg"
                                header={label}
                                extraBox={
                                    <p className="gsc-tool-sub chart-subtitle">
                                        {t('tools.gsc.trend.one_axis')}
                                    </p>
                                }
                                chartContainerStyles="gsc-tool-chart-heading"
                                chartData={this.chartData(rows)}
                                leftAxisLabel={label}
                                leftAxisReversed={metric === 'position'}
                                customYLabelType={this.axisLabel}
                                showLabelAsPercentage={metric === 'ctr'}
                            />

                            <div className="gsc-tool-chart-foot">
                                <span>{t('tools.latest_hours.movement.axis_note')}</span>
                                <span>
                                    {t('tools.latest_hours.movement.plotted', {
                                        plotted: rows.length,
                                        comparable: totals.hours,
                                    })}
                                </span>
                            </div>

                            <details className="gsc-tool-details">
                                <summary>{t('tools.latest_hours.movement.view_table')}</summary>
                                <DataTable
                                    nodes={rows}
                                    COLUMNS={columns}
                                    sortFns={getSortKeys(columns)}
                                    additionalStyles={setAdditionalStyles(columns)}
                                    searchFunction={this.searchFunction}
                                    fileName={`${t(
                                        'tools.latest_hours.movement.title',
                                    )} — ${label}`}
                                    notUseDefaultStyle
                                    customContainerStyles="gsc-tool-trend-table"
                                />
                            </details>
                        </div>
                    )}

                    <p className="gsc-tool-table-note">
                        {t('tools.latest_hours.movement.totals_note')}
                    </p>
                </div>
            </div>
        );
    }
}

export default withTranslation()(HourlyMovement);
